import HomeOutlined from "@ant-design/icons/HomeOutlined";
import InfoCircleOutlined from "@ant-design/icons/InfoCircleOutlined";
import MailOutlined from "@ant-design/icons/MailOutlined";
import UserOutlined from "@ant-design/icons/UserOutlined";

import { ReactCardCarousel } from "@libs/card-carousal/card-carousal";
import { Validated_AccountCreateRequest } from "@sdk/user-management/user-management.controller-models";
import { Button, Divider, Form, Input, Layout, Space, Tooltip } from "antd";
import { useForm } from "antd/es/form/Form";
import { PillSelector } from "components/common/pill-selector/pill-selector";
import { PasswordStrengthIndicators } from "components/modules/user-management/users/components/my-profile/components/password-strength-indicator";
import { push } from "connected-react-router";
import debounce from "debounce-promise";
import queryString from "query-string";
import { useCallback, useEffect, useMemo, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useStore } from "react-redux";
import { useHistory } from "react-router";
import { addRecentLoginData } from "store/modules/app-state/app-state.actions";
import { setPreviousDomain } from "store/modules/preferences/preferences.actions";
import { setDarkMode } from "store/modules/ui-state/ui-state.actions";
import { isWorkEmail } from "utils/is-email";
import { justWait } from "utils/just-wait";
import { ToAlphaNumeric } from "utils/to-alphanumeric";
import { toKebabCase } from "utils/to-kebab-case";
import { SDK } from "../../../@sdk/sdk";
import { useSDKActionWithDeps } from "../../../@sdk/sdk.hooks";
import {
  setAccessToken,
  setCurrentUser,
} from "../../../store/modules/users/users.actions";
import { HiveFiveIcon } from "./components/hive-five";
import { TestimonialCard } from "./components/testimonial-card";
import { Testimonials } from "./data/testimonials";
import "./register.page.style.scss";

const { Footer } = Layout;

const debouncedDomainAvailability = debounce(
  async (domain) => {
    try {
      const status = await SDK.checkDomainAvailability(domain);
      if (!status.availability) {
        throw "Domain is already taken";
      }
      return;
    } catch (e) {
      console.log("e", e);
      throw "Domain is already taken";
    }
  },
  1000,
  { leading: false, trailing: true },
);

const RegisterPage = () => {
  const store = useStore();

  const goToDashboard = useCallback(() => store.dispatch(push("/")), [store]);
  const goToLogin = useCallback(() => store.dispatch(push("/login")), [store]);
  const [registerForm] = useForm();
  const {
    doAction: onFinish,
    isProcessing,
    response,
    dispatch,
  } = useSDKActionWithDeps(
    () => ({
      action: (SDK) => (values: Validated_AccountCreateRequest) => {
        return SDK.createAccount({
          ...values,
          domain: values.domain.toLowerCase(),
          email: values.email.toLowerCase(),
        }).then((res) => {
          const enableSubDomains =
            !window.location.href.includes("localhost") &&
            !window.location.href.includes("vercel.app");

          if (enableSubDomains) {
            window.location.href = `https://${res.organization.domain}.clickconnector.io/login?accessToken=${res.accessToken}`;
            return justWait(4000);
          } else {
            SDK.configure({ token: res.accessToken });
            dispatch(setPreviousDomain(res.organization.domain));
            dispatch(setCurrentUser(res.user));
            dispatch(setAccessToken(res.accessToken));
            dispatch(
              addRecentLoginData({
                // Org Data
                domain: res.organization.domain,
                // organizationLogo: string,
                // User Data
                email: res.user.credentials.email,
                firstName: res.user.data.firstName,
                lastName: res.user.data.lastName,
                avatar: res.user.data.avatar,
                // Access Data
                accessToken: res.accessToken,
              }),
            );
          }
          return res;
          // Patch
          // setTimeout(() => {
          //   window.location.reload();
          // }, 500);
        });
      },
      successMessage: "Successfully registered",
      failureMessage: "Something went wrong",
    }),
    [],
  );
  const [IsEmailReadOnly, setIsEmailReadOnly] = useState(true);

  const history = useHistory();

  const formInitialValues = useMemo(() => {
    const query = queryString.parse(history?.location?.search || "");
    return query;
  }, [history?.location?.search]);

  const [step, setStep] = useState(2);

  useEffect(() => {
    store.dispatch(setDarkMode(false));
  }, [store]);

  return (
    <Layout className="layout w-full flex flex-col  min-h-full md:flex-row bg-white">
      <div className="bg-white dark:bg-gray-900 p-8 lg:p-24 rounded-md bold-form-labels  mode_transition flex flex-row justify-center">
        {step === 1 && (
          <div style={{ width: 300 }}>
            <div className="font-bold text-4xl ">Hey 👋</div>

            <div className="text-gray-600">
              Let's get to know you so that you can start managing your
              organization
            </div>
            <Button type="primary" className="font-bold">
              Let's Start
            </Button>
          </div>
        )}
        {step === 2 && (
          <>
            <Form
              name="register_form"
              className="register-form"
              initialValues={formInitialValues}
              onFinish={onFinish}
              layout="vertical"
              form={registerForm}
              requiredMark={false}
            >
              <div className="text-2xl font-bold mb-4">🚀 Get Started</div>

              <Space>
                {/* First Name */}

                <Form.Item
                  name="firstName"
                  label="First Name"
                  rules={[
                    {
                      required: true,
                      message: "Please input your first name!",
                    },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined className="site-form-item-icon" />}
                    placeholder="First name"
                  />
                </Form.Item>

                {/* Last Name */}

                <Form.Item
                  name="lastName"
                  label="Last Name"
                  rules={[
                    { required: true, message: "Please input your last name!" },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined className="site-form-item-icon" />}
                    placeholder="Last name"
                  />
                </Form.Item>
              </Space>

              {/* Email */}

              <Form.Item
                name="email"
                hasFeedback
                validateFirst={true}
                rules={[
                  { type: "email", required: true },
                  ({ getFieldValue }) => ({
                    validator: (rule, value) => {
                      return new Promise((resolve, reject) => {
                        if (isWorkEmail(value)) {
                          return resolve("");
                        }
                        throw reject("Please enter your work email");
                      });
                    },
                  }),
                ]}
                label="Work Email"
              >
                <Input
                  prefix={<MailOutlined className="site-form-item-icon" />}
                  placeholder="Email"
                  readOnly={IsEmailReadOnly}
                  onFocus={() => {
                    setIsEmailReadOnly(false);
                  }}
                />
              </Form.Item>

              {/* Password */}
              <Form.Item
                name="password"
                label="Password"
                rules={[
                  {
                    required: true,
                    message: "Please input your password!",
                  },
                ]}
                // hasFeedback
              >
                <Input.Password placeholder="Password" />
              </Form.Item>

              <PasswordStrengthIndicators />

              <Divider>
                <b>Workspace Details</b>
              </Divider>

              <Form.Item
                name="name"
                label="Workspace Name / Organization Name / Team Name"
                rules={[{ required: true, message: "Please enter a name!" }]}
              >
                <Input
                  prefix={<i className="ri-ancient-pavilion-line"></i>}
                  placeholder="Workspace Name / Organization name"
                  suffix={
                    <Tooltip title="A friendly name to identify your workspace">
                      <InfoCircleOutlined
                        style={{ color: "rgba(0,0,0,.45)" }}
                      />
                    </Tooltip>
                  }
                  onChange={(e) => {
                    registerForm.setFieldValue(
                      "domain",
                      toKebabCase(ToAlphaNumeric(e.target.value)),
                    );
                  }}
                />
              </Form.Item>

              {/* Domain */}

              <Form.Item
                name="domain"
                hasFeedback
                validateFirst={true}
                rules={[
                  { required: true, message: "Please select a domain" },
                  ({ getFieldValue }) => ({
                    validator: async (rule, value) =>
                      await debouncedDomainAvailability(value),
                  }),
                ]}
                label="Your Unique Workspace URL"
                help="A unique website address for your workspace"
              >
                <Input
                  prefix={<HomeOutlined className="site-form-item-icon" />}
                  placeholder="Your Unique Workspace URL"
                  addonAfter=".clickconnector.io"
                  // suffix={
                  //   <Tooltip title="A unique website address for your workspace">
                  //     <InfoCircleOutlined
                  //       style={{ color: "rgba(0,0,0,.45)" }}
                  //     />
                  //   </Tooltip>
                  // }
                />
              </Form.Item>

              <Form.Item
                name="captcha"
                rules={[{ required: true, message: "Please verify" }]}
                label="Security Check"
              >
                <ReCAPTCHA sitekey="6LdJ_uMUAAAAALR5jyd-BByM9MaUfrJpRBtdNomY" />
              </Form.Item>

              <Form.Item
                name="onboardingFlow"
                hasFeedback
                label="Select your workspace type"
              >
                <PillSelector
                  options={[
                    { value: "SAAS", label: "SaaS" },
                    { value: "TRAVEL_AGENCY", label: "Travel Agency" },
                  ]}
                />
              </Form.Item>

              {/* <SimpleCollapse title="Invitation Code">
                <Form.Item
                  name="inviteCode"
                  hasFeedback
                  validateFirst={true}
                  label="Invite Code"
                >
                  <Input
                    prefix={<i className="ri-coupon-line"></i>}
                    placeholder="Eg: ZZZZZZ"
                  />
                </Form.Item>
              </SimpleCollapse> */}

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  className="login-form-button mt-4 font-bold"
                  loading={isProcessing}
                  icon={<i className="ri-arrow-right-circle-fill"></i>}
                  size="large"
                >
                  Get in
                </Button>
                Or{" "}
                <Button type="link" onClick={() => goToLogin()}>
                  Login now!
                </Button>
              </Form.Item>
            </Form>
          </>
        )}
        {step === 3 && <></>}
      </div>
      <div className="left-side-section hidden md:flex flex-col justify-center items-center  register-page-info-panel border-l border-gray-200  rounded-md p-12 mode_transition flex-1">
        <img
          src="/cc-assets/logo.png"
          className="sm:my-2"
          style={{ width: "8rem" }}
          alt="ClickConnector"
        />
        <HiveFiveIcon size={"8rem" as any} />
        <div className="text-2xl font-bold">You've made the right choice!</div>
        <div className="text-gray-600 dark:text-gray-400 mode_transition mb-8">
          Transform Your Customer Relationship with the right Software
        </div>
        <div style={{ width: "50rem", height: "25rem" }} className="relative">
          <ReactCardCarousel
            autoplay={true}
            autoplay_speed={6000}
            disable_box_shadow={true}
            alignment={"vertical"}
            spread="wide"
          >
            {Testimonials.map((testimonial, index) => (
              <div key={index.toString()} style={{ width: 600 }}>
                <TestimonialCard data={testimonial} />
              </div>
            ))}
          </ReactCardCarousel>
        </div>

        <Footer
          style={{ textAlign: "center", background: "transparent" }}
          className="hidden sm:block"
        >
          ClickConnector ©{new Date().getFullYear()}
        </Footer>
      </div>
    </Layout>
  );
};

export default RegisterPage;
